import { Grid } from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'

import { useTitle } from 'hooks'
import { getLongFormatDate } from 'utils/dates'
import history from 'utils/history'
import { trackPageView, trackFunderProfileTabView } from 'utils/mixpanel'

import { isOpenToRequestsUnknown } from 'components/OpenToRequestsSelect/helpers'
import ReportProfileError from './components/ReportProfileError'
import ProfileCard from 'components/ProfileCard'
import DissolvedProfileCard from 'components/ProfileCard/DissolvedProfileCard'
import ProfileAbout from 'components/ProfileAbout'
import ProfilePrograms from 'components/ProfilePrograms'
import ProfileGifts from 'components/ProfileGifts'
import ProfileGrantFit from 'components/ProfileGrantFit'

import { getFunderById } from 'store/actions/profile'
import { getNotesByFunderId } from 'store/actions/notes'
import { getPipelineStages } from 'store/actions/pipelineStages'
import {
  getGiftHistoryCancelSource,
  resetPaginatedGiftHistory,
  getFunderPaginatedGiftHistory,
  DEFAULT_PAGINATION_PARAMS
} from 'store/actions/giftHistory'
import {
  selectFunderProfileInfo,
  selectIsFunderProfileDissolved,
  selectIsFunderProfileLoading
} from 'store/selectors/profile'
import { selectHasExecutePermissions, selectUser } from 'store/selectors/user'

const TABS = [ProfileAbout, ProfilePrograms, ProfileGifts, ProfileGrantFit]

const ProfilePage = () => {
  const dispatch = useDispatch()
  const { profileId, tab, extra } = useParams()
  const { i18n } = useTranslation()
  const t = i18n.getResourceBundle(i18n.language)

  const isLoading = useSelector(selectIsFunderProfileLoading)
  const isFunderDissolved = useSelector(selectIsFunderProfileDissolved)
  const GiftCancelSource = getGiftHistoryCancelSource()
  const { name, funderPrograms, updatedAt, openToRequests, hasGifts } =
    useSelector(selectFunderProfileInfo)
  const user = useSelector(selectUser)
  const hasExecutePermissions = useSelector(selectHasExecutePermissions)

  const [programIndex, setProgramIndex] = useState(0)

  const isUnknown = isOpenToRequestsUnknown(openToRequests, t)
  const showPrograms = Array.isArray(funderPrograms) && funderPrograms.length && !isUnknown

  const formattedLastUpdated = !!updatedAt && getLongFormatDate(updatedAt)

  const enabledTabs = isLoading
    ? TABS
    : TABS.filter(tab =>
        tab.isEnabled({ isFunderDissolved, hasExecutePermissions, showPrograms, hasGifts, user })
      )

  const tabIndex = tab ? enabledTabs.findIndex(item => item.tabPath === tab) : 0

  const gotoTab = (tab, extra) => {
    const extraParams = extra ? `/${extra.join('/')}` : ''
    history.push(
      `${history.location.pathname.split('/').slice(0, 3).join('/')}/${tab.tabPath}${extraParams}`
    )
  }

  useTitle(() => name, [name])

  useEffect(() => {
    if (tabIndex < 0) {
      gotoTab(ProfileAbout)
    }
  }, [enabledTabs])

  // Currently, extra is only used for programId, will need to be updated if extra has other uses
  useEffect(() => {
    if (isLoading) return
    const id = +extra
    const index = funderPrograms.findIndex(program => program.id === id)
    if (index >= 0) {
      setProgramIndex(index)
    } else {
      gotoTab(enabledTabs[tabIndex])
    }
  }, [extra, funderPrograms, isLoading])

  useEffect(() => {
    trackPageView()
  }, [])

  useEffect(() => {
    dispatch(getFunderById(profileId))
    dispatch(
      getFunderPaginatedGiftHistory(
        GiftCancelSource,
        profileId,
        DEFAULT_PAGINATION_PARAMS.resultsPerPage,
        DEFAULT_PAGINATION_PARAMS.currentPage,
        DEFAULT_PAGINATION_PARAMS.filterYear,
        DEFAULT_PAGINATION_PARAMS.filterFocus,
        DEFAULT_PAGINATION_PARAMS.fromGiftValue,
        DEFAULT_PAGINATION_PARAMS.toGiftValue,
        DEFAULT_PAGINATION_PARAMS.sortField,
        DEFAULT_PAGINATION_PARAMS.sortDirection
      )
    )

    if (hasExecutePermissions) {
      dispatch(getPipelineStages())
      dispatch(getNotesByFunderId(profileId))
    }

    return () => {
      dispatch(resetPaginatedGiftHistory(GiftCancelSource))
    }
  }, [profileId])

  useEffect(() => {
    if (tabIndex >= 0 && tabIndex < enabledTabs.length) {
      trackFunderProfileTabView(enabledTabs[tabIndex].tabPath)
    }
  }, [tab])

  return (
    <div className="main profile-page">
      <Grid container>
        <ReportProfileError />
      </Grid>
      <Grid container>{isFunderDissolved ? <DissolvedProfileCard /> : <ProfileCard />}</Grid>
      {!!formattedLastUpdated && (
        <div className="profile-last-updated">{`${t.funder.profileUpdated}: ${formattedLastUpdated}`}</div>
      )}
      {!isLoading && (
        <Tabs
          selectedIndex={tabIndex}
          onSelect={index => {
            const targetTab = enabledTabs[index]
            const programId = funderPrograms?.[programIndex]?.id
            const extraParam = targetTab.requiresProgramParam && programId ? [programId] : undefined
            gotoTab(targetTab, extraParam)
          }}
        >
          <TabList>
            {enabledTabs.map(ProfileTab => (
              <Tab key={ProfileTab.tabPath}>{ProfileTab.getTabName(t)}</Tab>
            ))}
          </TabList>
          {enabledTabs.map(ProfileTab => (
            <TabPanel key={ProfileTab.tabPath}>
              <ProfileTab gotoTab={gotoTab} programIndex={programIndex} />
            </TabPanel>
          ))}
        </Tabs>
      )}
    </div>
  )
}

export default ProfilePage
